"use client";

import { useState } from "react";
import Link from "next/link";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useSubscription } from "@/hooks/useSubscription";
import { useCheckout } from "@/hooks/useCheckout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon } from "@/components/icons";
import { TRIAL_DAYS } from "@/lib/billing/plans";
import type { SubscriptionStatus } from "@/lib/billing/plans";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(msEpoch: number): string {
  return new Date(msEpoch).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function daysUntil(msEpoch: number): number {
  return Math.max(0, Math.ceil((msEpoch - Date.now()) / (1000 * 60 * 60 * 24)));
}

// ─── Status badge mapping ─────────────────────────────────────────────────────

const STATUS_BADGE: Record<
  SubscriptionStatus,
  { label: string; variant: "success" | "secondary" | "destructive" | "outline" }
> = {
  active: { label: "Active", variant: "success" },
  trialing: { label: "Trial", variant: "secondary" },
  past_due: { label: "Past Due", variant: "destructive" },
  canceled: { label: "Canceled", variant: "outline" },
  canceling: { label: "Canceling", variant: "secondary" },
  paused: { label: "Paused", variant: "outline" },
  none: { label: "No Plan", variant: "outline" },
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SubscriptionStatusPanel
 *
 * Dashboard-embedded widget showing the current user's Polar.sh subscription
 * state. Reads from Convex via the `useSubscription()` hook, so it re-renders
 * automatically when subscription state changes (e.g. after a webhook event).
 *
 * Features:
 * - Trial countdown with days remaining and progress bar
 * - Current plan tier and billing cycle dates
 * - Direct Polar.sh checkout CTA (trial or paid upgrade)
 * - Post-subscription success confirmation state
 * - Past-due payment warning with update-payment CTA
 * - Grace period countdown
 * - Admin override display
 */
export function SubscriptionStatusPanel({
  showSuccess = false,
}: {
  /** Pass true when the user just completed checkout (e.g. ?subscription=success) */
  showSuccess?: boolean;
}) {
  const {
    isLoading,
    tier,
    status,
    planName,
    hasAccess,
    isTrialing,
    trialEndsAt,
    hasUsedTrial,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    gracePeriod,
    isAdmin,
    serverLimit,
    activeOrgCount,
  } = useSubscription();

  const openPortal = useAction(api.actions.openCustomerPortal);
  const { startCheckout, loadingPlan, error: checkoutError } = useCheckout();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");

  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Subscription</CardTitle>
          <Skeleton className="h-5 w-16 rounded-full" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-52" />
          <Skeleton className="h-9 w-40" />
        </CardContent>
      </Card>
    );
  }

  // ── Derived state ──
  const hasSubscription = tier !== "free" && status !== "none";
  const badgeInfo = isAdmin
    ? { label: "Admin", variant: "success" as const }
    : cancelAtPeriodEnd && hasSubscription
      ? { label: "Canceling", variant: "outline" as const }
      : STATUS_BADGE[status];

  // Trial days remaining + progress
  const daysRemaining = isTrialing && trialEndsAt ? daysUntil(trialEndsAt) : null;
  const trialProgressPct =
    isTrialing && daysRemaining !== null
      ? Math.max(0, Math.min(100, ((TRIAL_DAYS - daysRemaining) / TRIAL_DAYS) * 100))
      : null;

  // ── Portal handler ──
  const handleManageSubscription = async () => {
    setPortalError("");
    setPortalLoading(true);
    try {
      const result = await openPortal({});
      if (!result.ok) {
        throw new Error(result.error?.message || "Unable to open customer portal.");
      }
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      setPortalError(
        err instanceof Error ? err.message : "Unable to open customer portal."
      );
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Checkout handler ──
  const handleUpgrade = () =>
    startCheckout("plus", {
      withTrial: !hasUsedTrial && !hasSubscription,
      successRedirect: "/dashboard?subscription=success",
    });

  // ── Renewal / expiry line ──
  let cycleLine: string | null = null;
  if (isTrialing && trialEndsAt && daysRemaining !== null) {
    cycleLine =
      daysRemaining === 0
        ? `Trial ends today (${formatDate(trialEndsAt)})`
        : `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining — ends ${formatDate(trialEndsAt)}`;
  } else if (cancelAtPeriodEnd && currentPeriodEnd) {
    const days = daysUntil(currentPeriodEnd);
    cycleLine =
      days === 0
        ? `Access expires today (${formatDate(currentPeriodEnd)})`
        : `Access expires in ${days} day${days !== 1 ? "s" : ""} — ${formatDate(currentPeriodEnd)}`;
  } else if (gracePeriod.isInGracePeriod && gracePeriod.graceEndsAt) {
    const days = daysUntil(gracePeriod.graceEndsAt);
    cycleLine =
      days === 0
        ? "Grace period ends today — renew now to keep access"
        : `Grace period: ${days} day${days !== 1 ? "s" : ""} left before access is removed`;
  } else if (currentPeriodEnd && hasSubscription) {
    cycleLine = `Renews ${formatDate(currentPeriodEnd)}`;
  }

  // ── Conditional flags ──
  const showPastDueWarning = status === "past_due" && !isAdmin;
  const showReactivateCTA =
    cancelAtPeriodEnd &&
    currentPeriodEnd &&
    currentPeriodEnd > Date.now() &&
    !isAdmin;
  const showGraceReactivate =
    gracePeriod.isInGracePeriod && !hasSubscription && !isAdmin;

  // ── Success confirmation ──
  const successBanner = showSuccess ? (
    <div className="flex items-start gap-3 rounded-lg border border-green-300 bg-green-50 p-3 dark:border-green-700 dark:bg-green-950/20">
      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
      <div className="text-sm text-green-800 dark:text-green-300">
        <p className="font-medium">Subscription activated!</p>
        <p className="mt-0.5 text-xs opacity-80">
          Your payment was successful. Premium features are now active.
        </p>
      </div>
    </div>
  ) : null;

  // ── No subscription — upgrade CTA ──
  if (!hasSubscription && !isAdmin && !gracePeriod.isInGracePeriod) {
    const canTrial = !hasUsedTrial;
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Subscription</CardTitle>
          <Badge variant="outline">Free</Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          {successBanner}
          <p className="text-sm text-muted">
            {canTrial
              ? "Start a free 7-day trial to unlock server management, Server Finder listings, and plugin integrations."
              : "Subscribe to unlock server management, Server Finder listings, and plugin integrations."}
          </p>
          {(checkoutError || portalError) && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {checkoutError || portalError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleUpgrade}
              disabled={loadingPlan !== null}
            >
              {loadingPlan === "plus"
                ? "Loading..."
                : canTrial
                  ? "Start Free Trial"
                  : "Upgrade Now"}
            </Button>
            <Link href="/pricing">
              <Button size="sm" variant="outline">
                View All Plans
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div className="space-y-0.5">
          <CardTitle className="text-base">Subscription</CardTitle>
          <p className="text-sm text-muted">
            {planName ?? (isAdmin ? "Admin access" : "Free")}
          </p>
        </div>
        <Badge variant={badgeInfo.variant}>{badgeInfo.label}</Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Post-subscription confirmation */}
        {successBanner}

        {/* Trial progress bar */}
        {isTrialing && trialProgressPct !== null && daysRemaining !== null && (
          <div>
            <div className="mb-1.5 flex items-center justify-between text-xs text-muted">
              <span>Trial progress</span>
              <span className="font-medium text-foreground">
                {daysRemaining} {daysRemaining === 1 ? "day" : "days"} remaining
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-foreground/10">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${trialProgressPct}%` }}
                role="progressbar"
                aria-valuenow={Math.round(trialProgressPct)}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}

        {/* Cycle / renewal / expiry line */}
        {cycleLine && (
          <p className="text-sm text-muted">{cycleLine}</p>
        )}

        {/* Usage summary */}
        {hasSubscription && serverLimit > 0 && (
          <div className="text-sm">
            <span className="text-muted">
              Organizations:{" "}
              <span className="font-medium text-foreground">{activeOrgCount}</span>
              {" / "}
              {serverLimit}
            </span>
          </div>
        )}

        {/* Trial upgrade nudge (shown when > 3 days remain; ≤3 days is the SubscriptionBanner's job) */}
        {isTrialing && daysRemaining !== null && daysRemaining > 3 && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              Enjoying your trial?{" "}
              <button
                className="font-medium underline underline-offset-2 hover:opacity-80"
                onClick={handleUpgrade}
                disabled={loadingPlan !== null}
              >
                {loadingPlan === "plus" ? "Loading..." : "Upgrade now"}
              </button>{" "}
              to keep access after it ends.
            </p>
          </div>
        )}

        {/* Past due warning */}
        {showPastDueWarning && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-xs font-medium text-red-800 dark:text-red-200">
              Payment failed
            </p>
            <p className="mt-0.5 text-xs text-red-700 dark:text-red-300">
              Update your payment method to restore uninterrupted access.
            </p>
          </div>
        )}

        {/* Grace period warning */}
        {showGraceReactivate && (
          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-900/20">
            <p className="text-xs font-medium text-orange-800 dark:text-orange-200">
              Subscription expired
            </p>
            <p className="mt-0.5 text-xs text-orange-700 dark:text-orange-300">
              Reactivate now to keep your servers and data.
            </p>
          </div>
        )}

        {/* Checkout / portal error */}
        {(checkoutError || portalError) && (
          <p className="text-xs text-red-600 dark:text-red-400">
            {checkoutError || portalError}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {hasSubscription && (
            <Button
              size="sm"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? "Opening..." : "Manage Subscription"}
            </Button>
          )}

          {/* Upgrade CTA for trialing users */}
          {isTrialing && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleUpgrade}
              disabled={loadingPlan !== null}
            >
              {loadingPlan === "plus" ? "Loading..." : "Upgrade Now"}
            </Button>
          )}

          {showReactivateCTA && (
            <Link href="/dashboard/subscription">
              <Button size="sm" variant="outline">
                Reactivate
              </Button>
            </Link>
          )}

          {showGraceReactivate && (
            <Button
              size="sm"
              onClick={handleUpgrade}
              disabled={loadingPlan !== null}
            >
              {loadingPlan === "plus" ? "Loading..." : "Reactivate Now"}
            </Button>
          )}

          {showPastDueWarning && (
            <Button
              size="sm"
              variant="destructive"
              onClick={handleManageSubscription}
              disabled={portalLoading}
            >
              {portalLoading ? "Opening..." : "Update Payment"}
            </Button>
          )}

          <Link href="/dashboard/billing">
            <Button size="sm" variant="ghost">
              Billing Details
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
